import { useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_hilanta-wellness/artifacts/yzd7rzdb_76b87d66-fa32-4fab-93bc-4ef8dc416573.jpg";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message envoyé avec succès !');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen py-20" data-testid="contact-page">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-sage-100 rounded-full flex items-center justify-center">
              <CheckCircle className="text-sage" size={40} />
            </div>
            <h1 className="font-heading text-3xl text-warm-brown mb-4">
              Message envoyé !
            </h1>
            <p className="font-body text-warm-brown-light mb-8">
              Merci de nous avoir contactés. Nous vous répondrons dans les plus brefs délais.
            </p>
            <Button
              onClick={() => {
                setSubmitted(false);
                setFormData({ name: '', email: '', phone: '', message: '' });
              }}
              className="bg-terracotta hover:bg-terracotta-600 text-white rounded-full px-8 py-4 font-body"
              data-testid="send-another-message"
            >
              Envoyer un autre message
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="contact-page">
      <Helmet>
        <title>Contact | Hilanta - Massage & Bien-être</title>
        <meta name="description" content="Contactez Hilanta pour toute question sur nos massages et soins bien-être. Téléphone : 09 60 42 18 22. Ouvert du mardi au samedi de 10h à 18h." />
      </Helmet>
      
      {/* Header */}
      <section className="py-8 bg-sand">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-4xl md:text-5xl text-warm-brown mb-4">
            Contactez-nous
          </h1>
          <p className="font-body text-warm-brown-light">
            Une question ? N'hésitez pas à nous écrire ou à nous appeler
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl shadow-lg p-8">
              <h2 className="font-heading text-2xl text-warm-brown mb-6">
                Envoyez-nous un message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="font-body text-warm-brown mb-2 block">
                    Nom complet *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Votre nom"
                    className="rounded-xl border-sand focus:border-terracotta"
                    data-testid="contact-input-name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="font-body text-warm-brown mb-2 block">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="votre@email.com"
                    className="rounded-xl border-sand focus:border-terracotta"
                    data-testid="contact-input-email"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="font-body text-warm-brown mb-2 block">
                    Téléphone
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="06 00 00 00 00"
                    className="rounded-xl border-sand focus:border-terracotta"
                    data-testid="contact-input-phone"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="font-body text-warm-brown mb-2 block">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Votre message..."
                    rows={5}
                    className="rounded-xl border-sand focus:border-terracotta resize-none"
                    data-testid="contact-input-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-terracotta hover:bg-terracotta-600 text-white rounded-full py-4 font-body"
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={18} />
                      Envoyer le message
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <div className="bg-terracotta rounded-3xl p-8 text-white mb-8">
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src={LOGO_URL} 
                    alt="Hilanta" 
                    className="w-16 h-16 rounded-full object-cover border-2 border-white/30"
                  />
                  <div>
                    <h2 className="font-heading text-2xl">HILANTA</h2>
                    <p className="text-white/80 text-sm">Massages & Bien-être</p>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-white/20 rounded-full">
                      <MapPin size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Adresse</h3>
                      <p className="text-white/90">
                        58 Avenue Maréchal Foch<br />
                        13004 Marseille
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-white/20 rounded-full">
                      <Phone size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Téléphone</h3>
                      <a href="tel:0960421822" className="text-white/90 hover:text-white" data-testid="contact-info-phone">
                        09 60 42 18 22
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-white/20 rounded-full">
                      <Mail size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Email</h3>
                      <a href="mailto:la.hilanta@gmail.com" className="text-white/90 hover:text-white" data-testid="contact-info-email">
                        la.hilanta@gmail.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transport */}
              <div className="bg-sand rounded-3xl p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="text-sage" size={24} />
                  <h3 className="font-heading text-xl text-warm-brown">Correspondances</h3>
                </div>
                <ul className="font-body text-warm-brown-light space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-terracotta rounded-full"></span>
                    Tramway 2 : Foch Boisson
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-sage rounded-full"></span>
                    Métro 1 : La Blancarde
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-gold rounded-full"></span>
                    Gare : Marseille Blancarde
                  </li>
                </ul>
              </div>

              {/* Map */}
              <div className="mt-8 rounded-3xl overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2903.5!2d5.4!3d43.3!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDPCsDE4JzAwLjAiTiA1wrAyNCcwMC4wIkU!5e0!3m2!1sfr!2sfr!4v1600000000000!5m2!1sfr!2sfr"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Hilanta Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
